import s3URL from '@mathflat/common/util/s3'
import React, { useMemo } from 'react'

import { buttonStyle, dotStyle } from './Button.style'
import { IconTextButton, TextButton } from './TextButton'

type DefaultProps = {
  LeftIcon?: React.ReactElement
  RightIcon?: React.ReactElement
  loading?: boolean
  width?: React.CSSProperties['width']
  minWidth?: React.CSSProperties['minWidth']
  withDotBadge?: boolean
} & React.ComponentPropsWithoutRef<'button'>

export type OutlineProps = DefaultProps & {
  theme: Extract<ButtonTheme, 'outline'>
  size?: ButtonSize
  color?: Exclude<ButtonColor, 'white'>
}

type GhostProps = DefaultProps & {
  theme: Extract<ButtonTheme, 'ghost'>
  size?: Exclude<ButtonSize, 'xs'>
  color?: ButtonColor
}

type LightSolidProps = DefaultProps & {
  theme: Exclude<ButtonTheme, 'outline' | 'ghost'>
  size?: ButtonSize
  color?: Exclude<ButtonColor, 'white'>
}

type NormalProps = DefaultProps & {
  theme: Exclude<ButtonTheme, 'outline' | 'ghost'>
  size?: Exclude<ButtonSize, 'xs'>
  color?: Exclude<ButtonColor, 'white'>
}

export type Props = NormalProps | OutlineProps | GhostProps | LightSolidProps

export type ButtonTheme =
  | 'primary'
  | 'ghost'
  | 'outline'
  | 'light-solid'
  | 'text'
  | 'square'
  | 'ellipsis'
  | 'transparent'

export type ButtonSize = 'xs' | 's' | 'm' | 'l'
export type ButtonColor = 'blue' | 'red' | 'dark' | 'white'
export type ButtonType = 'button' | 'text' | 'icon-square' | 'icon-ellipse' | 'icon-transparent'

/**
 *
 * @deprecated
 */
const _Button = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      size = 'l',
      loading,
      children,
      LeftIcon,
      RightIcon,
      role = 'button',
      minWidth,
      withDotBadge,
      ...props
    },
    ref,
  ) => {
    const { theme, color, width, style } = props

    const _children = useMemo(() => {
      if (loading) {
        let imgurl = 'spin.gif'
        if (props.theme === 'primary' || (props.theme === 'ghost' && props.color === 'white')) {
          imgurl = 'spin-white.gif'
        }
        return (
          <img
            width={24}
            height={24}
            src={s3URL.mathflatAssetsBucket.common(`images/${imgurl}`)}
            alt="로딩 중"
          />
        )
      }

      const LeftElement = (() => {
        if (!LeftIcon) return <></>

        return <span style={{ marginRight: 2 }}>{LeftIcon}</span>
      })()

      const RightElement = (() => {
        if (!RightIcon) return <></>

        return <span style={{ marginLeft: 2 }}>{RightIcon}</span>
      })()

      return (
        <>
          {LeftElement}
          {children}
          {RightElement}
        </>
      )
    }, [loading, children, LeftIcon, RightIcon])

    return (
      <button
        {...props}
        ref={ref}
        css={buttonStyle[theme]({ size, color })}
        role={role}
        disabled={loading || props.disabled}
        aria-busy={loading}
        aria-disabled={props.disabled}
        style={{
          ...(width && { width }),
          ...(minWidth && { minWidth }),
          ...(withDotBadge && { position: 'relative' }),
          ...style,
        }}
      >
        {_children}
        {withDotBadge && <div css={dotStyle}></div>}
      </button>
    )
  },
) as React.ForwardRefExoticComponent<
  React.PropsWithoutRef<Props> & React.RefAttributes<HTMLButtonElement>
> & { Text: typeof TextButton; IconText: typeof IconTextButton }

_Button.displayName = 'Button'

_Button.Text = TextButton
_Button.IconText = IconTextButton
export default _Button
